// AssetsSection.tsx — onglet « Assets » du Dashboard : les derniers fichiers posés
// dans le mediaboard des régies
//
// Refait le 2026-10-17 d'après la maquette validée par l'utilisateur
// (https://claude.ai/artifact/MtJ8i9W8i9G2uHH3CcnL7N), sur le modèle de l'onglet
// Chaînes. Dans chaque jour, les assets sont rangés PAR RÉGIE : on voit d'un coup
// ce qu'un créateur prépare pour son direct (fonds, incrustations, jingles) plutôt
// qu'une suite de fichiers sans lien. Chaque carte dit où l'asset sert (bouton média,
// fond ou incrustation d'une scène). Données : admin-api DashboardMediaboardAssets
// (`assets` et `assetStats`).

import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';
import { API_URLS } from '../../../backendServices/urls';
import { Geo } from './ActivityOverview';
import { ListTable, TitleCell, CreatorCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Owner, Segment, usePicked, ViewToggle, EmptyState, useView, groupByDay, hour, clock, size, sizeParts,
         plural, tintOf, openChannel, creatorPlace, CREATOR_PLACE_HINT } from './dashboardShared';

export type AssetKind  = 'image' | 'video' | 'sound';
export type AssetPlace = 'media' | 'bg' | 'inc';

export interface DashboardAsset {
  key             : string;
  kind            : AssetKind;
  name            : string;
  originalName    : string;
  configId        : number;
  filename        : string;
  channelId       : number;
  channelName     : string;
  owner           : string;
  ownerEmail      : string;
  creationDate    : string | null;
  sizeBytes       : number;
  format          : string;
  durationSeconds : number | null;
  place           : AssetPlace;
  placeLabel      : string;
  geo?            : Geo | null;
}

export interface AssetStats {
  today       : number;
  todayRegies : number;
  week        : Record<AssetKind, number>;
  bytes       : Record<AssetKind, number>;
  totalBytes  : number;
  heavyCount  : number;
  heaviest    : { name: string; sizeBytes: number; owner: string } | null;
}

export type AssetKpi = 'today' | 'week' | 'space' | 'heavy' | null;

interface Props {
  assets   : DashboardAsset[];
  kpi?     : AssetKpi;
  onKpi?   : (kpi: AssetKpi) => void;
  stats    : AssetStats | null;
  loading  : boolean;
  error    : boolean;
  filtered : boolean;
  onClearFilters : () => void;
  onOpen   : (a: DashboardAsset) => void;
  /** Graphe, carte et villes de la période (ActivityOverview), placés sous les chiffres. */
  overview?: React.ReactNode;
  /** Éléments du point désigné sur la carte : surlignés, le premier amené à l'écran. */
  picked?: DashboardAsset[];
}

const VIEW_KEY    = 'dashboardV2.assetsView';
export const HEAVY_BYTES = 200 * 1024 * 1024;   // comme DashboardMediaboardAssets::HEAVY_BYTES
const TYPE_LABEL: Record<AssetKind, string> = { image: 'IMAGE', video: 'VIDÉO', sound: 'SON' };
const TYPE_CLASS: Record<AssetKind, string> = { image: tabs.tImage, video: tabs.tVideo, sound: tabs.tSound };

/** Le fichier lui-même, servi par la régie : l'API cherche <nom>.* — le nom SANS extension. */
export const assetSrc = (a: { configId: number; filename: string }) =>
  `${API_URLS.getRegieConfigs}/${a.configId}/media?filename=${encodeURIComponent(String(a.filename).replace(/\.[^.]+$/, ''))}`;

/** Même fichier, en pièce jointe sous son nom d'origine (admin-api RegieMediaDownload, `dl` / `as`). */
const assetDownloadUrl = (a: DashboardAsset) =>
  `${assetSrc(a)}&dl=1&as=${encodeURIComponent(a.originalName || a.name || a.filename)}`;

const hashOf = (s: string) => Array.from(s).reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7);

// ── Petits morceaux ───────────────────────────────────────────────────────────

/** Forme d'onde décorative, stable pour un même fichier : un son n'a pas d'image. */
const Wave: React.FC<{ seed: number }> = ({ seed }) => {
  let x = Math.abs(seed) % 233280;
  const bars = Array.from({ length: 40 }, (_, i) => {
    x = (x * 9301 + 49297) % 233280;
    const h = 12 + (x / 233280) * 76;
    return <rect key={i} x={i * 5 + 1} y={(100 - h) / 2} width="3" height={h} rx="1.5" fill="currentColor" />;
  });
  return <svg className={tabs.wave} viewBox="0 0 200 100" preserveAspectRatio="none" aria-hidden="true">{bars}</svg>;
};

const PlaceIcon: React.FC<{ place: AssetPlace }> = ({ place }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    {place === 'media' && <>
      <rect x="1" y="1" width="5" height="5" rx="1" fill="currentColor"/><rect x="8" y="1" width="5" height="5" rx="1" fill="none" stroke="currentColor"/>
      <rect x="1" y="8" width="5" height="5" rx="1" fill="none" stroke="currentColor"/><rect x="8" y="8" width="5" height="5" rx="1" fill="none" stroke="currentColor"/>
    </>}
    {place === 'bg'  && <rect x="1" y="2" width="12" height="10" rx="1.5" fill="currentColor"/>}
    {place === 'inc' && <><rect x="1" y="2" width="12" height="10" rx="1.5" fill="none" stroke="currentColor"/><rect x="7" y="7" width="5" height="4" rx="1" fill="currentColor"/></>}
  </svg>
);

const Placement: React.FC<{ a: DashboardAsset }> = ({ a }) => (
  <span className={tabs.placement} title={a.placeLabel}><PlaceIcon place={a.place} /><span>{a.placeLabel}</span></span>
);

const Thumb: React.FC<{ a: DashboardAsset }> = ({ a }) => {
  const [ failed, setFailed ] = useState(false);
  const heavy = a.sizeBytes > HEAVY_BYTES;
  return (
    <div className={`${styles.thumb} ${a.kind === 'sound' ? tabs.soundThumb : ''}`} style={a.kind === 'sound' ? undefined : { background: tintOf(hashOf(a.key)) }}>
      {a.kind === 'sound' && <Wave seed={hashOf(a.key)} />}
      {a.kind === 'image' && !failed && <img src={assetSrc(a)} alt="" loading="lazy" onError={() => setFailed(true)} />}
      {a.kind === 'video' && !failed && <video className={tabs.thumbVideo} src={`${assetSrc(a)}#t=0.1`} preload="metadata" muted playsInline onError={() => setFailed(true)} />}
      {a.kind !== 'sound' && failed && <span className={styles.thumbName}>{a.name}</span>}
      <span className={`${tabs.typeTag} ${TYPE_CLASS[a.kind]}`}>{TYPE_LABEL[a.kind]}</span>
      {a.durationSeconds != null && <span className={tabs.corner}>{a.kind === 'sound' ? '♪' : '▶'} {clock(a.durationSeconds)}</span>}
      {heavy && <span className={`${tabs.cornerL} ${tabs.amber}`}>{size(a.sizeBytes)}</span>}
    </div>
  );
};

/** Vignette de la vue liste : l'image, la première image de la vidéo, ou l'onde d'un son. */
const MiniThumb: React.FC<{ a: DashboardAsset }> = ({ a }) => {
  const [ failed, setFailed ] = useState(false);
  if (a.kind === 'sound') return <div className={tabs.soundThumb} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Wave seed={hashOf(a.key)} /></div>;
  return (
    <div style={{ width: '100%', height: '100%', background: tintOf(hashOf(a.key)) }}>
      {!failed && a.kind === 'image' && <img src={assetSrc(a)} alt="" loading="lazy" onError={() => setFailed(true)} />}
      {!failed && a.kind === 'video' && <video src={`${assetSrc(a)}#t=0.1`} preload="metadata" muted playsInline onError={() => setFailed(true)} />}
    </div>
  );
};

// ── Composant ────────────────────────────────────────────────────────────────

const AssetsSection: React.FC<Props> = ({ assets, stats, loading, error, filtered, onClearFilters, onOpen, overview, picked, kpi, onKpi }) => {
  const { ref: pickRef, mark } = usePicked(picked, (a: DashboardAsset) => a.key);
  const [ kind,  setKind  ] = useState<'all' | AssetKind>('all');
  const [ place, setPlace ] = useState<'all' | AssetPlace>('all');
  const [ view, setView ] = useView(VIEW_KEY);

  const shown = useMemo(() => assets.filter(a =>
    (kind === 'all' || a.kind === kind) && (place === 'all' || a.place === place)
  ), [assets, kind, place]);

  // Jour, puis régie dans le jour (dans l'ordre d'arrivée du plus récent).
  const days = useMemo(() => groupByDay(shown, a => a.creationDate).map(d => {
    const regies: { configId: number; items: DashboardAsset[] }[] = [];
    for (const a of d.items) {
      const r = regies.find(x => x.configId === a.configId) ?? (regies.push({ configId: a.configId, items: [] }), regies[regies.length - 1]);
      r.items.push(a);
    }
    return { title: d.title, count: d.items.length, regies };
  }), [shown]);

  const week      = stats ? stats.week.image + stats.week.video + stats.week.sound : 0;
  const weekTotal = Math.max(1, week);
  const [ totalValue, totalUnit ] = sizeParts(stats?.totalBytes ?? 0);

  return (
    <div className={styles.root} ref={pickRef}>

      {/* ── Chiffres ── */}
      <div className={styles.kpis}>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'today' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'today'}
                title="Cliquer pour ne montrer que ces fichiers (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'today' ? null : 'today')}>
          <span className={styles.kpiLabel}>Déposés aujourd'hui</span>
          <span className={styles.kpiValue}>{stats?.today ?? '–'}</span>
          {stats && <span className={styles.kpiMuted}>{stats.today > 0 ? `sur ${plural(stats.todayRegies, 'régie')}` : 'rien pour l’instant'}</span>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'week' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'week'}
                title="Cliquer pour ne montrer que les fichiers déposés sur 7 jours (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'week' ? null : 'week')}>
          <span className={styles.kpiLabel}>Par type, 7 jours</span>
          <span className={styles.kpiValue}>{stats ? week : '–'}</span>
          {stats && <>
            <span className={tabs.split} aria-hidden="true">
              <span className={tabs.cImage} style={{ width: `${stats.week.image / weekTotal * 100}%` }}/>
              <span className={tabs.cVideo} style={{ width: `${stats.week.video / weekTotal * 100}%` }}/>
              <span className={tabs.cSound} style={{ width: `${stats.week.sound / weekTotal * 100}%` }}/>
            </span>
            <span className={styles.originLegend}>
              <span><i className={tabs.cImage}/>Images {stats.week.image}</span>
              <span><i className={tabs.cVideo}/>Vidéos {stats.week.video}</span>
              <span><i className={tabs.cSound}/>Sons {stats.week.sound}</span>
            </span>
          </>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${kpi === 'space' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'space'}
                title="Cliquer pour montrer tous les fichiers des mediaboards, toutes dates (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'space' ? null : 'space')}>
          <span className={styles.kpiLabel}>Espace des mediaboards</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow}`}>{stats ? <>{totalValue}<small>{totalUnit}</small></> : '–'}</span>
          {stats && stats.totalBytes > 0 && <span className={styles.kpiMuted}>dont {size(stats.bytes.video) || '0 o'} de vidéos</span>}
        </button>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${stats && stats.heavyCount > 0 ? tabs.kpiWarn : ''} ${kpi === 'heavy' ? styles.kpiOn : ''}`} aria-pressed={kpi === 'heavy'}
                title="Cliquer pour ne montrer que les fichiers de plus de 200 Mo, toutes dates (liste, carte, villes)" onClick={() => onKpi?.(kpi === 'heavy' ? null : 'heavy')}>
          <span className={styles.kpiLabel}>Fichiers lourds (plus de 200 Mo)</span>
          <span className={`${styles.kpiValue} ${stats && stats.heavyCount > 0 ? tabs.amber : ''}`}>{stats?.heavyCount ?? '–'}</span>
          {stats?.heaviest
            ? <span className={styles.kpiMuted}>le plus gros : {size(stats.heaviest.sizeBytes)}{stats.heaviest.owner ? `, ${stats.heaviest.owner}` : ''}</span>
            : stats && <span className={styles.kpiMuted}>aucun</span>}
        </button>
      </div>

      {overview}

      {/* ── Filtres de l'onglet ── */}
      <div className={styles.toolbar}>
        <Segment label="Type" value={kind} onChange={setKind}
                 choices={[ ['all', 'Tous'], ['image', 'Images'], ['video', 'Vidéos'], ['sound', 'Sons'] ] as const} />
        <Segment label="Emplacement" value={place} onChange={setPlace}
                 choices={[ ['all', 'Partout'], ['media', 'Boutons médias'], ['bg', 'Fonds de scène'], ['inc', 'Incrustations'] ] as const} />
        <span className={styles.spacer}/>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {error && <div className={styles.empty}>Chargement impossible.</div>}

      {!error && shown.length === 0 &&
        <EmptyState loading={loading} title="Aucun asset sur cette période"
                    hint="Élargissez la période ou retirez un filtre. Les fichiers posés dans un mediaboard apparaissent ici, relevés chaque minute."
                    canClear={filtered || kind !== 'all' || place !== 'all'}
                    onClear={() => { setKind('all'); setPlace('all'); onClearFilters(); }} />}

      {/* ── Vue cartes : jour, puis régie ── */}
      {!error && view === 'cards' && days.map(d => (
        <section key={d.title} className={styles.day}>
          <h3 className={styles.dayTitle}>{d.title} <span className={styles.muted}>{plural(d.count, 'asset')}</span></h3>
          {d.regies.map(r => {
            const first = r.items[0], last = r.items[r.items.length - 1];
            return (
              <div key={r.configId} className={tabs.group}>
                <div className={tabs.groupHead}>
                  <Owner owner={first.owner} email={first.ownerEmail} small />
                  <strong>{first.channelName}</strong>
                  <span>{plural(r.items.length, 'fichier')}{r.items.length > 1 ? ` · de ${hour(last.creationDate)} à ${hour(first.creationDate)}` : ` · ${hour(first.creationDate)}`}</span>
                  <span className={styles.spacer}/>
                  <button type="button" className={styles.primary} onClick={() => openChannel(first.channelId, 'regie')}>Ouvrir la régie</button>
                </div>
                <div className={tabs.cardsTight}>
                  {r.items.map(a => (
                    <article key={a.key} {...mark(a)} className={`${styles.card} ${tabs.clickable} ${a.sizeBytes > HEAVY_BYTES ? tabs.cardWarn : ''}`}
                             role="button" tabIndex={0} onClick={() => onOpen(a)}
                             onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(a); } }}
                             aria-label={`Agrandir ${a.name}`}>
                      <Thumb a={a} />
                      <div className={styles.cardBody}>
                        <span className={styles.cardName} title={a.originalName || a.name}>{a.name}</span>
                        <Placement a={a} />
                        <div className={tabs.cardMeta}><span>{hour(a.creationDate)}</span><span>{a.format}</span><span>{size(a.sizeBytes)}</span>{creatorPlace(a.geo) && <span title={CREATOR_PLACE_HINT}>📍 {creatorPlace(a.geo)}</span>}</div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      ))}

      {/* ── Vue liste ── */}
      {!error && view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={a => a.key} rowProps={a => mark(a, true)} dateOf={a => a.creationDate}
                   noun={{ one: 'asset', many: 'assets' }} columns={[
          { id: 'asset', label: 'Asset', cell: a =>
              <TitleCell thumb={<MiniThumb a={a} />} title={a.name} titleHint={a.originalName || a.name}
                         sub={<>{a.channelName} · <Placement a={a} /></>} /> },
          { id: 'type', label: 'Type', cell: a => <span className={`${tabs.typeTag} ${tabs.typeInline} ${TYPE_CLASS[a.kind]}`}>{TYPE_LABEL[a.kind]}</span> },
          { id: 'creator', label: 'Créateur', cell: a => <CreatorCell owner={a.owner} email={a.ownerEmail} geo={a.geo} /> },
          { id: 'format', label: 'Format', hide: 'lg', cell: a => a.format || <Dash/> },
          { id: 'size', label: 'Taille', right: true, sort: (x, y) => y.sizeBytes - x.sizeBytes,
            cell: a => <span className={a.sizeBytes > HEAVY_BYTES ? tabs.amber : undefined}>{size(a.sizeBytes) || '—'}</span> },
          { id: 'duration', label: 'Durée', right: true, hide: 'md', sort: (x, y) => (y.durationSeconds ?? -1) - (x.durationSeconds ?? -1),
            cell: a => clock(a.durationSeconds) || <Dash/> },
          { id: 'date', label: 'Déposé', cell: a => <WhenCell iso={a.creationDate} /> },
          { id: 'actions', label: '', right: true, cell: a =>
              <RowActions>
                <div className={styles.actions}>
                  <button type="button" className={styles.primary} onClick={() => onOpen(a)}>Agrandir</button>
                  {/* Télécharger (2026-10-24) à la place du bouton Régie : `dl=1` fait servir le
                      fichier en pièce jointe par admin-api (autre origine que le front, où
                      l'attribut `download` est ignoré), `as` lui donne son nom d'origine. */}
                  <a className={styles.secondary} href={assetDownloadUrl(a)} download rel="noreferrer"
                     title={`Télécharger ${a.originalName || a.name}`}
                     style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                    <Download size={14} aria-hidden="true" />Télécharger
                  </a>
                </div>
              </RowActions> },
        ]} />
      )}
    </div>
  );
};

export default AssetsSection;
